import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pencil, Trash2, Search, Repeat, FileSpreadsheet, Printer, Plus, X, Receipt, Paperclip, Download, Upload, Check, CheckCheck, Clock, AlertTriangle, Loader2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Link2, Columns3, ListFilter, Copy, Sparkles } from 'lucide-react';
import api from '../services/api';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import Paginacion from '../components/Paginacion';
import AutocompleteProducto from '../components/AutocompleteProducto';
import Modal from '../components/Modal';
import TarjetaIngresoEgreso from '../components/TarjetaIngresoEgreso';
import BotonAyuda from '../components/BotonAyuda';
import { etiquetaCategoria } from '../utils/categorias';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

function fechaHoy() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatCostoIA(uso) {
  if (!uso) return '';
  const totalTokens = uso.tokensEntrada + uso.tokensSalida;
  return ` (${totalTokens.toLocaleString('es')} tokens ≈ $${uso.costoUsd.toFixed(4)})`;
}

// Agrupa las categorías (planas + sub-categorías vía padre_id) por categoría de primer nivel,
// para mostrarlas como <optgroup> en los selects de categoría.
const TAMANIO_PAGINA_TRANSITO = 25;

// Columnas opcionales de la grilla (#82): el usuario elige cuáles ver y la
// elección se recuerda por usuario en localStorage. Fecha, Descripción,
// Monto y Acciones son fijas y no aparecen acá.
const COLUMNAS_OPCIONALES = [
  { id: 'cuenta', label: 'Cuenta' },
  { id: 'categoria', label: 'Categoría' },
  { id: 'tipo', label: 'Tipo' },
  { id: 'estado', label: 'Estado' },
  { id: 'cliente', label: 'Cliente' },
  { id: 'referencia', label: 'Referencia' },
  { id: 'registrado_por', label: 'Registrado por' }
];
const COLUMNAS_VISIBLES_DEFECTO = { cuenta: true, categoria: true, tipo: true, estado: true, cliente: false, referencia: false, registrado_por: false };

const ESTADOS_FILTRO = [
  { value: '', label: 'Todos los estados' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'confirmado', label: 'Confirmado' },
  { value: 'conciliado', label: 'Conciliado' }
];

function claveColumnasVisibles(usuarioId) {
  return `movimientos_columnas_${usuarioId || 'anon'}`;
}

function cargarColumnasVisibles(usuarioId) {
  try {
    const guardado = window.localStorage.getItem(claveColumnasVisibles(usuarioId));
    if (!guardado) return COLUMNAS_VISIBLES_DEFECTO;
    return { ...COLUMNAS_VISIBLES_DEFECTO, ...JSON.parse(guardado) };
  } catch (err) {
    return COLUMNAS_VISIBLES_DEFECTO;
  }
}

// Metáfora de "un check / dos checks" (como en apps de mensajería): confirmado
// a mano es un check simple, conciliado contra el banco es el doble check.
const ESTADO_MOVIMIENTO_INFO = {
  pendiente: { label: 'Pendiente', clases: 'bg-amber-50 text-amber-700 hover:bg-amber-100', Icono: Clock },
  confirmado: { label: 'Confirmado', clases: 'bg-slate-100 text-slate-600 hover:bg-slate-200', Icono: Check },
  conciliado: { label: 'Conciliado', clases: 'bg-primary-50 text-primary-700', Icono: CheckCheck }
};

function agruparCategorias(categorias, tipo) {
  const delTipo = categorias.filter((c) => c.tipo === tipo);
  const principales = delTipo.filter((c) => !c.padre_id);
  return principales.map((padre) => {
    const hijos = delTipo.filter((c) => c.padre_id === padre.id);
    return { nombre: etiquetaCategoria(padre), items: hijos.length > 0 ? [padre, ...hijos] : [padre] };
  });
}

export default function Movimientos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { empresaActiva, empresas, usuario, rolActual } = useAuth();
  // Editar, eliminar y cambiar la categoría en lote los exige el servidor a
  // contador o más; al operador no se le muestran (las transferencias sí,
  // porque esas las puede editar y eliminar).
  const puedeEditarMov = ['contador', 'gerente', 'admin'].includes(rolActual);
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;
  const [movimientos, setMovimientos] = useState([]);
  const cuentaImpresionUnica =
    movimientos.length > 0 && movimientos.every((m) => m.cuenta_nombre === movimientos[0].cuenta_nombre)
      ? movimientos[0].cuenta_nombre
      : null;
  const [cuentas, setCuentas] = useState([]);
  const [cuentasTodas, setCuentasTodas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState({ cuenta_id: '', categoria_id: '', cliente_id: '', tipo: 'ingreso', monto: '', tasa_override: '', descripcion: '', fecha: fechaHoy() });
  const [error, setError] = useState('');
  const [archivoAdjunto, setArchivoAdjunto] = useState(null);
  const [extraidoIA, setExtraidoIA] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [avisoIA, setAvisoIA] = useState('');
  const [productos, setProductos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  // Reglas de categorización (#80): la categoría que propuso una regla o la IA
  // en el formulario; elegir otra a mano lo borra.
  const [reglaForm, setReglaForm] = useState(null);
  const [iaForm, setIaForm] = useState(null);
  const [pidiendoIAForm, setPidiendoIAForm] = useState(false);
  const [itemsFactura, setItemsFactura] = useState([]);
  const [proveedorFactura, setProveedorFactura] = useState({ modo: 'ninguno', id: '', nombre: '' });
  const [adjuntos, setAdjuntos] = useState([]);
  const [duplicados, setDuplicados] = useState(null);
  const [mostrarModalEdoCuenta, setMostrarModalEdoCuenta] = useState(false);
  const [analizandoEdoCuenta, setAnalizandoEdoCuenta] = useState(false);
  const [contadorEdoCuenta, setContadorEdoCuenta] = useState(0);
  const [errorEdoCuenta, setErrorEdoCuenta] = useState('');
  const [filasTransito, setFilasTransito] = useState([]);
  const [paginaTransito, setPaginaTransito] = useState(0);
  const [procesandoTodas, setProcesandoTodas] = useState(false);
  const [filtroTransito, setFiltroTransito] = useState('');
  const [categoriaMasivaTransito, setCategoriaMasivaTransito] = useState('');
  const [hojasExcel, setHojasExcel] = useState(null);
  const [analizandoExcel, setAnalizandoExcel] = useState(false);
  const [errorExcel, setErrorExcel] = useState('');
  const [archivoPdfPendiente, setArchivoPdfPendiente] = useState(null);
  const [archivoExcelPendiente, setArchivoExcelPendiente] = useState(null);
  const [usoIAEdoCuenta, setUsoIAEdoCuenta] = useState(null);
  const [mostrarTransferencia, setMostrarTransferencia] = useState(false);
  const [editandoTransferenciaId, setEditandoTransferenciaId] = useState(null);
  const [formT, setFormT] = useState({
    cuenta_origen_id: '',
    cuenta_destino_id: '',
    monto_origen: '',
    monto_destino: '',
    fecha: fechaHoy(),
    descripcion: ''
  });
  const [errorT, setErrorT] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [cuentaFiltro, setCuentaFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [orden, setOrden] = useState({ campo: 'fecha', dir: 'desc' });
  const [columnasVisibles, setColumnasVisibles] = useState(() => cargarColumnasVisibles(usuario?.id));
  const [mostrarColumnas, setMostrarColumnas] = useState(false);
  const [mostrarFiltroEstado, setMostrarFiltroEstado] = useState(false);
  const [resumen30, setResumen30] = useState({ ingresos: 0, egresos: 0 });
  const [resumenConciliacion, setResumenConciliacion] = useState({ total: 0, conciliados: 0, por_conciliar: 0, porcentaje: 0 });
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total, setTotal] = useState(0);
  const [exportando, setExportando] = useState(false);
  // Selección múltiple + acciones en lote (#83). Solo la página actual de la
  // grilla (no todos los resultados filtrados); se limpia en cada recarga.
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [categoriaLote, setCategoriaLote] = useState('');
  const [aplicandoLote, setAplicandoLote] = useState(false);
  const [reciboId, setReciboId] = useState(null);
  const recibo = movimientos.find((m) => m.id === reciboId) || null;
  const movimientoEditando = editandoId ? movimientos.find((m) => m.id === editandoId) : null;
  const [tasasPorFecha, setTasasPorFecha] = useState({});
  // Mismo selector BCV/USD de los reportes, recordado (#115); acá se usa en minúscula.
  const [vistaMoneda, setVistaMoneda] = useVistaMoneda();
  const vistaTasa = vistaMoneda.toLowerCase();
  const cuentaFormSel = cuentas.find((c) => String(c.id) === String(form.cuenta_id));
  const clienteFormNombre = clientes.find((c) => String(c.id) === String(form.cliente_id))?.nombre || '';

  // Al escribir un movimiento nuevo sin categoría: si una regla coincide, la
  // pone y muestra "Por regla «X»". Lo elegido a mano manda.
  useEffect(() => {
    if (!mostrarForm || editandoId) return undefined;
    if (form.categoria_id && !reglaForm) return undefined;
    const texto = `${form.descripcion || ''}${clienteFormNombre}`.trim();
    if (texto.length < 2) {
      if (reglaForm) { setForm((f) => ({ ...f, categoria_id: '' })); setReglaForm(null); }
      return undefined;
    }
    const t = setTimeout(() => {
      api.post('/categorizacion-reglas/sugerir', {
        movimientos: [{ descripcion: form.descripcion, nombre_receptor: clienteFormNombre, tipo: form.tipo, monto: form.monto, cuenta_id: form.cuenta_id }]
      }).then(({ data }) => {
        const r = data[0];
        if (r?.categoria_id) {
          setForm((f) => ({ ...f, categoria_id: String(r.categoria_id) }));
          setReglaForm({ id: r.regla_id, texto: r.regla_texto, categoria_id: String(r.categoria_id) });
          setIaForm(null);
        } else if (reglaForm) {
          setForm((f) => ({ ...f, categoria_id: '' }));
          setReglaForm(null);
        }
      }).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarForm, editandoId, form.descripcion, clienteFormNombre, form.tipo, form.monto, form.cuenta_id]);

  async function sugerirCategoriaIA() {
    setPidiendoIAForm(true);
    setError('');
    try {
      const { data } = await api.post('/categorizacion-reglas/sugerir-ia', {
        descripcion: form.descripcion, nombre_receptor: clienteFormNombre, tipo: form.tipo,
        monto: form.monto, fecha: form.fecha, moneda: cuentaFormSel?.moneda
      });
      if (data.categoria_id) {
        setForm((f) => ({ ...f, categoria_id: String(data.categoria_id) }));
        setIaForm({ categoria_id: String(data.categoria_id), motivo: data.motivo });
      } else {
        setIaForm({ categoria_id: '', motivo: 'La IA no encontró una categoría que encaje.' });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'La IA no respondió; intenta de nuevo.');
    } finally {
      setPidiendoIAForm(false);
    }
  }
  const cuentaFormEsBS = cuentaFormSel?.moneda === 'BS';
  const hayMovimientosBS = movimientos.some((m) => m.cuenta_moneda === 'BS');
  const cuentaFiltroSel = cuentas.find((c) => String(c.id) === String(cuentaFiltro));
  const simboloMonedaFiltro = cuentaFiltroSel?.moneda === 'BS' ? 'Bs ' : '$';
  // Una cuenta inactiva se sigue consultando, pero no admite movimientos,
  // transferencias ni cargas de estado de cuenta nuevas (#147).
  const cuentaFiltroInactiva = Boolean(cuentaFiltroSel) && Number(cuentaFiltroSel.activo) === 0;

  function valorConvertido(m) {
    if (m.cuenta_moneda !== 'BS') return Number(m.monto);
    const tasaDia = tasasPorFecha[m.fecha];
    if (!tasaDia) return null;
    const tasa = vistaTasa === 'bcv' ? tasaDia.tasa_bcv : Number(m.tasa_override) || tasaDia.tasa_usd_generica;
    if (!tasa) return null;
    return Number(m.monto) / Number(tasa);
  }

  // Al crear, la cuenta origen sale de la empresa activa; al editar, la
  // transferencia puede haberse originado en otra empresa, así que se busca
  // entre todas las cuentas del corporativo.
  const opcionesCuentaOrigen = editandoTransferenciaId ? cuentasTodas : cuentas;
  const cuentaOrigenSel = opcionesCuentaOrigen.find((c) => String(c.id) === String(formT.cuenta_origen_id));
  const cuentaDestinoSel = cuentasTodas.find((c) => String(c.id) === String(formT.cuenta_destino_id));
  const monedaDistinta = Boolean(cuentaOrigenSel && cuentaDestinoSel && cuentaOrigenSel.moneda !== cuentaDestinoSel.moneda);

  async function cargar() {
    const [m, c, cat, ct, prod, prov, cli] = await Promise.all([
      api.get('/movimientos', {
        params: {
          q: busqueda || undefined,
          cuenta_id: cuentaFiltro || undefined,
          estado: estadoFiltro || undefined,
          orden_por: orden.campo,
          orden_dir: orden.dir,
          pagina
        }
      }),
      api.get('/cuentas'),
      api.get('/categorias'),
      api.get('/cuentas/todas'),
      api.get('/productos'),
      api.get('/proveedores'),
      api.get('/clientes')
    ]);
    setMovimientos(m.data.datos);
    setTotalPaginas(m.data.totalPaginas);
    setTotal(m.data.total);
    setSeleccionados(new Set());
    setCuentas(c.data);
    setCategorias(cat.data);
    setCuentasTodas(ct.data);
    setProductos(prod.data);
    setProveedores(prov.data);
    setClientes(cli.data);
    cargarResumenes();

    // El endpoint de tasas trae por defecto solo las últimas 90 (backend),
    // así que si la página muestra movimientos más viejos que eso (ej. datos
    // históricos recién importados) hay que pedir explícitamente el rango de
    // fechas que aparece en pantalla para no mostrar "—" en la columna BCV/USD.
    const fechas = m.data.datos.map((mov) => mov.fecha);
    if (fechas.length > 0) {
      const desde = fechas.reduce((a, b) => (b < a ? b : a));
      const hasta = fechas.reduce((a, b) => (b > a ? b : a));
      const { data: tc } = await api.get('/tasas-cambio', { params: { desde, hasta } });
      setTasasPorFecha(Object.fromEntries(tc.map((t) => [t.fecha, t])));
    } else {
      setTasasPorFecha({});
    }
  }

  async function cargarResumenes() {
    if (!cuentaFiltro) return;
    const [periodo, conciliacion] = await Promise.all([
      api.get('/movimientos/resumen-periodo', { params: { cuenta_id: cuentaFiltro, dias: 30 } }),
      api.get('/movimientos/resumen-conciliacion', { params: { cuenta_id: cuentaFiltro } })
    ]);
    setResumen30(periodo.data);
    setResumenConciliacion(conciliacion.data);
  }

  useEffect(() => {
    if (!cuentaFiltro && cuentas.length > 0) {
      setCuentaFiltro((cuentas.find((c) => Number(c.activo) === 1) || cuentas[0]).id);
    }
  }, [cuentas, cuentaFiltro]);

  useEffect(() => {
    if (mostrarForm && !editandoId) {
      setForm((f) => ({ ...f, cuenta_id: cuentaFiltro }));
    }
  }, [cuentaFiltro, mostrarForm, editandoId]);

  useEffect(() => {
    if (mostrarTransferencia && !editandoTransferenciaId) {
      setFormT((f) => ({ ...f, cuenta_origen_id: cuentaFiltro }));
    }
  }, [cuentaFiltro, mostrarTransferencia, editandoTransferenciaId]);

  useEffect(() => {
    if (!empresaActiva) return;
    setPagina(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, busqueda, cuentaFiltro, estadoFiltro, orden.campo, orden.dir]);

  useEffect(() => {
    if (!empresaActiva) return;
    const controlador = setTimeout(() => cargar(), 300);
    return () => clearTimeout(controlador);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, busqueda, cuentaFiltro, estadoFiltro, orden.campo, orden.dir, pagina]);

  async function empezarEdicion(m) {
    cancelarTransferencia();
    setEditandoId(m.id);
    setForm({
      cuenta_id: m.cuenta_id,
      categoria_id: m.categoria_id || '',
      cliente_id: m.cliente_id || '',
      tipo: m.tipo,
      monto: m.monto,
      tasa_override: m.tasa_override || '',
      descripcion: m.descripcion || '',
      fecha: m.fecha
    });
    setArchivoAdjunto(null);
    setExtraidoIA(null);
    setAvisoIA('');
    setDuplicados(null);
    setAdjuntos([]);
    setItemsFactura([]);
    setProveedorFactura({ modo: 'ninguno', id: '', nombre: '' });
    setMostrarForm(true);
    try {
      const { data } = await api.get('/adjuntos', { params: { movimiento_id: m.id } });
      setAdjuntos(data);
    } catch (err) {
      // si falla la carga de adjuntos no bloquea la edición del movimiento
    }
  }

  // Duplicar (#83): precarga el formulario de "nuevo movimiento" con los
  // datos del original (misma fecha) para que el usuario revise/ajuste antes
  // de guardar — no crea nada hasta que confirme. Al no setear editandoId,
  // `guardar()` hace un POST normal, igual que un alta manual.
  function duplicarMovimiento(m) {
    cancelarTransferencia();
    setEditandoId(null);
    setForm({
      cuenta_id: m.cuenta_id,
      categoria_id: m.categoria_id || '',
      cliente_id: m.cliente_id || '',
      tipo: m.tipo,
      monto: m.monto,
      tasa_override: m.tasa_override || '',
      descripcion: m.descripcion || '',
      fecha: m.fecha
    });
    setArchivoAdjunto(null);
    setExtraidoIA(null);
    setAvisoIA('');
    setDuplicados(null);
    setAdjuntos([]);
    setItemsFactura([]);
    setProveedorFactura({ modo: 'ninguno', id: '', nombre: '' });
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setEditandoId(null);
    setForm({ cuenta_id: cuentaFiltro, categoria_id: '', cliente_id: '', tipo: 'ingreso', monto: '', tasa_override: '', descripcion: '', fecha: fechaHoy() });
    setArchivoAdjunto(null);
    setExtraidoIA(null);
    setAvisoIA('');
    setAdjuntos([]);
    setDuplicados(null);
    setItemsFactura([]);
    setProveedorFactura({ modo: 'ninguno', id: '', nombre: '' });
    setReglaForm(null);
    setIaForm(null);
  }

  function abrirFormMovimiento() {
    cancelarTransferencia();
    setEditandoId(null);
    setReglaForm(null);
    setIaForm(null);
    setForm({ cuenta_id: cuentaFiltro, categoria_id: '', cliente_id: '', tipo: 'ingreso', monto: '', tasa_override: '', descripcion: '', fecha: fechaHoy() });
    setArchivoAdjunto(null);
    setExtraidoIA(null);
    setAvisoIA('');
    setAdjuntos([]);
    setDuplicados(null);
    setItemsFactura([]);
    setProveedorFactura({ modo: 'ninguno', id: '', nombre: '' });
    setMostrarForm(true);
  }

  function abrirTransferencia() {
    cancelar();
    setEditandoTransferenciaId(null);
    setFormT({ cuenta_origen_id: cuentaFiltro, cuenta_destino_id: '', monto_origen: '', monto_destino: '', fecha: fechaHoy(), descripcion: '' });
    setErrorT('');
    setMostrarTransferencia(true);
  }

  function cancelarTransferencia() {
    setMostrarTransferencia(false);
    setEditandoTransferenciaId(null);
    setFormT({ cuenta_origen_id: cuentaFiltro, cuenta_destino_id: '', monto_origen: '', monto_destino: '', fecha: fechaHoy(), descripcion: '' });
    setErrorT('');
  }

  useEffect(() => {
    const accion = searchParams.get('accion');
    const cuentaId = searchParams.get('cuenta_id');
    const q = searchParams.get('q');
    if (cuentaId) setCuentaFiltro(cuentaId);
    if (q) setBusqueda(q);
    if (accion === 'nuevo') abrirFormMovimiento();
    else if (accion === 'transferencia') abrirTransferencia();
    if (accion || cuentaId || q) setSearchParams({}, { replace: true });
    // Depende de searchParams: el buscador general puede traer otros
    // parámetros estando ya en Movimientos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  async function empezarEdicionTransferencia(m) {
    try {
      const { data } = await api.get(`/transferencias/${m.transferencia_id}`);
      cancelar();
      setEditandoTransferenciaId(data.id);
      setFormT({
        cuenta_origen_id: data.cuenta_origen_id,
        cuenta_destino_id: data.cuenta_destino_id,
        monto_origen: data.monto_origen,
        monto_destino: data.monto_destino,
        fecha: data.fecha,
        descripcion: data.descripcion || ''
      });
      setErrorT('');
      setMostrarTransferencia(true);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cargar la transferencia');
    }
  }

  async function guardarTransferencia(e) {
    e.preventDefault();
    setErrorT('');
    const payload = {
      cuenta_origen_id: formT.cuenta_origen_id,
      cuenta_destino_id: formT.cuenta_destino_id,
      monto_origen: formT.monto_origen,
      monto_destino: monedaDistinta ? formT.monto_destino : formT.monto_origen,
      fecha: formT.fecha,
      descripcion: formT.descripcion
    };
    try {
      if (editandoTransferenciaId) {
        await api.put(`/transferencias/${editandoTransferenciaId}`, payload);
      } else {
        await api.post('/transferencias', payload);
      }
      cancelarTransferencia();
      cargar();
    } catch (err) {
      setErrorT(err.response?.data?.error || 'Error al guardar la transferencia');
    }
  }

  async function eliminarTransferencia(transferenciaId) {
    if (!window.confirm('¿Eliminar esta transferencia? Se revertirá el saldo en ambas cuentas y quedará registrado en la bitácora.')) return;
    try {
      await api.delete(`/transferencias/${transferenciaId}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar la transferencia.');
    }
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');

    // Primer submit: chequea si ya existe un movimiento con la misma cuenta+fecha+monto.
    // Si aparece uno, se guarda en `duplicados` y se corta acá para que el usuario vea
    // el aviso; el siguiente click (botón "Guardar de todas formas") ya no repite el chequeo.
    if (duplicados === null) {
      try {
        const { data } = await api.get('/movimientos/verificar-duplicado', {
          params: { cuenta_id: form.cuenta_id, fecha: form.fecha, monto: form.monto, excluir_id: editandoId || undefined }
        });
        if (data.length > 0) {
          setDuplicados(data);
          return;
        }
        setDuplicados([]);
      } catch (err) {
        setDuplicados([]);
      }
    }

    try {
      let movimientoId = editandoId;
      if (editandoId) {
        await api.put(`/movimientos/${editandoId}`, form);
      } else {
        const { data } = await api.post('/movimientos', form);
        movimientoId = data.id;
        if (reglaForm && String(form.categoria_id) === reglaForm.categoria_id) {
          api.post(`/categorizacion-reglas/${reglaForm.id}/uso`).catch(() => {});
        }
      }
      if (movimientoId && extraidoIA) {
        // El archivo ya se subió al analizarlo con IA; solo hay que vincularlo.
        await api.post('/adjuntos/vincular', { movimiento_id: movimientoId, ...extraidoIA });
      } else if (movimientoId && archivoAdjunto) {
        const datosArchivo = new FormData();
        datosArchivo.append('movimiento_id', movimientoId);
        datosArchivo.append('archivo', archivoAdjunto);
        await api.post('/adjuntos', datosArchivo);
      }

      const itemsEnlazados = itemsFactura.filter((it) => it.producto_id && it.precio_unitario);
      if (movimientoId && itemsEnlazados.length > 0) {
        try {
          let proveedorId = proveedorFactura.modo === 'existente' ? proveedorFactura.id : null;
          if (proveedorFactura.modo === 'nuevo' && proveedorFactura.nombre.trim()) {
            const { data } = await api.post('/proveedores', { nombre: proveedorFactura.nombre.trim() });
            proveedorId = data.id;
          }
          // El proveedor de este insumo facturó en la moneda de la cuenta con la
          // que se pagó (BS o USD) — algunos proveedores facturan a BCV, otros a
          // USD; el costeo en vivo convierte los costos en BS con la tasa que se
          // pida en cada reporte (no queda fijo como los precios de venta).
          const monedaCosto = cuentaFormSel?.moneda === 'BS' ? 'BS' : 'USD';
          await Promise.all(itemsEnlazados.map((it) => api.post('/producto-costo-historial', {
            producto_id: it.producto_id,
            proveedor_id: proveedorId,
            movimiento_id: movimientoId,
            fecha: form.fecha,
            costo_unitario: it.precio_unitario,
            moneda: monedaCosto,
            origen: 'factura'
          })));
        } catch (err) {
          // No bloquea el guardado del movimiento si falla el registro de costo.
          console.error('No se pudo registrar el costo de algún producto:', err);
        }
      }

      cancelar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el movimiento');
    }
  }

  async function analizarConIA() {
    if (!archivoAdjunto) return;
    setAnalizando(true);
    setAvisoIA('');
    try {
      const datosArchivo = new FormData();
      datosArchivo.append('archivo', archivoAdjunto);
      const { data } = await api.post('/adjuntos/extraer', datosArchivo);
      setExtraidoIA({ archivo: data.archivo, nombre_archivo: data.nombre_archivo });
      setArchivoAdjunto(null);
      setDuplicados(null);
      if (data.datos) {
        setForm((f) => ({
          ...f,
          tipo: data.datos.tipo || f.tipo,
          fecha: data.datos.fecha || f.fecha,
          monto: data.datos.monto ?? f.monto,
          descripcion: data.datos.referencia
            ? `${data.datos.descripcion || ''} ref: ${data.datos.referencia}`.trim()
            : data.datos.descripcion || f.descripcion
        }));
        if (data.datos.items && data.datos.items.length > 0) {
          setItemsFactura(data.datos.items.map((it) => {
            const sugerido = productos.some((p) => p.id === it.producto_id_sugerido) ? it.producto_id_sugerido : null;
            return {
              descripcion: it.descripcion || '',
              cantidad: it.cantidad ?? '',
              precio_unitario: it.precio_unitario ?? '',
              producto_id: sugerido
            };
          }));
        }
        if (data.datos.proveedor) {
          const nombreProv = data.datos.proveedor.trim();
          const match = proveedores.find((p) => p.nombre.toLowerCase() === nombreProv.toLowerCase());
          setProveedorFactura(
            match ? { modo: 'existente', id: match.id, nombre: match.nombre } : { modo: 'nuevo', id: '', nombre: nombreProv }
          );
        }
        setAvisoIA(`Datos sugeridos por IA — revisalos antes de guardar.${formatCostoIA(data.uso)}`);
      } else {
        setAvisoIA(data.error_ia || 'No se pudo extraer los datos automáticamente, completá el formulario a mano.');
      }
    } catch (err) {
      setAvisoIA(err.response?.data?.error || 'No se pudo analizar el archivo.');
    } finally {
      setAnalizando(false);
    }
  }

  async function descargarAdjunto(adjunto) {
    try {
      const { data } = await api.get(`/adjuntos/${adjunto.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = adjunto.nombre_archivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('No se pudo descargar el adjunto');
    }
  }

  function abrirModalEdoCuenta() {
    cancelar();
    cancelarTransferencia();
    setErrorEdoCuenta('');
    setFilasTransito([]);
    setHojasExcel(null);
    setErrorExcel('');
    setArchivoPdfPendiente(null);
    setArchivoExcelPendiente(null);
    setUsoIAEdoCuenta(null);
    setMostrarModalEdoCuenta(true);
  }

  function cerrarModalEdoCuenta() {
    if (filasTransito.length > 0 && !window.confirm('Todavía hay movimientos sin aceptar ni rechazar en la tabla. ¿Cerrar de todas formas? No se van a guardar.')) {
      return;
    }
    setMostrarModalEdoCuenta(false);
    setFilasTransito([]);
    setErrorEdoCuenta('');
    setHojasExcel(null);
    setErrorExcel('');
    setArchivoPdfPendiente(null);
    setArchivoExcelPendiente(null);
    setUsoIAEdoCuenta(null);
  }

  async function chequearDuplicadoFila(id, fecha, monto, referencia) {
    try {
      const { data } = await api.get('/movimientos/verificar-duplicado', { params: { cuenta_id: cuentaFiltro, fecha, monto, referencia: referencia || undefined } });
      setFilasTransito((prev) => prev.map((f) => (f.id === id ? { ...f, duplicado: data.length > 0 } : f)));
    } catch (err) {
      setFilasTransito((prev) => prev.map((f) => (f.id === id ? { ...f, duplicado: false } : f)));
    }
  }

  function crearFilaTransito(m, i) {
    // Si el backend detectó un pago pendiente con la misma referencia
    // bancaria (pendiente #77 fase 2 / #79), el concepto sugerido ya viene
    // armado con la fórmula de la empresa (Acción + Persona + (Descripción)).
    // La referencia no se repite en la descripción: ya tiene su propia
    // columna en la tabla de revisión.
    const descripcionInicial = m.sugerencia
      ? m.sugerencia.concepto
      : m.descripcion || '';

    return {
      id: `${Date.now()}-${i}`,
      tipo: m.tipo === 'egreso' ? 'egreso' : 'ingreso',
      fecha: m.fecha || fechaHoy(),
      monto: m.monto ?? '',
      descripcion: descripcionInicial,
      referencia: m.referencia || '',
      categoria_id: m.categoria_id || '',
      sugerencia: m.sugerencia || null,
      // Checkbox "vincular este pago" — marcado por defecto cuando hay
      // sugerencia; el usuario puede destildarlo y el movimiento se crea
      // suelto, igual que si no hubiera match.
      vincularPago: !!m.sugerencia,
      duplicado: null,
      guardando: false
    };
  }

  function cargarFilasTransito(movimientos) {
    const filas = movimientos.map(crearFilaTransito);
    setFilasTransito(filas);
    setPaginaTransito(0);
    setFiltroTransito('');
    filas.forEach((f) => {
      if (f.fecha && f.monto !== '') chequearDuplicadoFila(f.id, f.fecha, f.monto, f.referencia);
    });
    // Reglas de categorización (#80): a las filas sin categoría les pone la
    // de la regla que coincida (se ve "Regla" al lado; se puede cambiar).
    const sinCat = filas.filter((f) => !f.categoria_id);
    if (sinCat.length) {
      api.post('/categorizacion-reglas/sugerir', {
        movimientos: sinCat.map((f) => ({ descripcion: f.descripcion, referencia: f.referencia, tipo: f.tipo, monto: f.monto, cuenta_id: cuentaFiltro }))
      }).then(({ data }) => {
        const porFila = new Map();
        data.forEach((r) => { if (r.categoria_id) porFila.set(sinCat[r.indice].id, r); });
        if (!porFila.size) return;
        setFilasTransito((prev) => prev.map((f) => {
          const r = porFila.get(f.id);
          return r && !f.categoria_id
            ? { ...f, categoria_id: String(r.categoria_id), regla: { id: r.regla_id, texto: r.regla_texto, categoria_id: String(r.categoria_id) } }
            : f;
        }));
      }).catch(() => {});
    }
  }

  function esperar(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Un estado de cuenta largo puede tardar más de un minuto en la IA, así que
  // el análisis arranca en background (job_id) y acá se consulta el estado
  // cada pocos segundos, en vez de esperar todo en un solo request que el
  // proxy/Apache podría cortar antes de tiempo.
  async function analizarEstadoCuenta(archivo) {
    setAnalizandoEdoCuenta(true);
    setErrorEdoCuenta('');
    setUsoIAEdoCuenta(null);
    setContadorEdoCuenta(0);
    try {
      const datosArchivo = new FormData();
      datosArchivo.append('archivo', archivo);
      const { data } = await api.post('/adjuntos/extraer-estado-cuenta/iniciar', datosArchivo);
      const jobId = data.job_id;

      let trabajo;
      do {
        await esperar(3000);
        ({ data: trabajo } = await api.get(`/adjuntos/extraer-estado-cuenta/estado/${jobId}`));
        setContadorEdoCuenta(trabajo.contador || 0);
      } while (trabajo.estado === 'procesando');

      if (trabajo.estado === 'error') {
        setErrorEdoCuenta(trabajo.error || 'No se pudo analizar el estado de cuenta');
        return;
      }

      cargarFilasTransito(trabajo.movimientos);
      setUsoIAEdoCuenta(trabajo.uso || null);
      setArchivoPdfPendiente(null);
    } catch (err) {
      setErrorEdoCuenta(err.response?.data?.error || 'No se pudo analizar el estado de cuenta');
    } finally {
      setAnalizandoEdoCuenta(false);
    }
  }

  async function analizarExcelEstadoCuenta(archivo) {
    if (!cuentaFiltro) {
      setErrorExcel('Seleccioná primero una cuenta arriba (filtro de Movimientos) para poder importar sus movimientos.');
      return;
    }
    setAnalizandoExcel(true);
    setErrorExcel('');
    setUsoIAEdoCuenta(null);
    try {
      const datosArchivo = new FormData();
      datosArchivo.append('archivo', archivo);
      const { data } = await api.post('/movimientos/importar-excel', datosArchivo);
      setHojasExcel(data.hojas);
      setArchivoExcelPendiente(null);
      // La importación puede haber creado categorías nuevas (a partir de la
      // columna SubCategoria del Excel) — refrescar para que el selector de
      // categoría de cada fila las tenga disponibles.
      const cat = await api.get('/categorias');
      setCategorias(cat.data);
    } catch (err) {
      setErrorExcel(err.response?.data?.error || 'No se pudo leer el archivo Excel');
    } finally {
      setAnalizandoExcel(false);
    }
  }

  function elegirHojaExcel(hoja) {
    cargarFilasTransito(hoja.movimientos);
    setHojasExcel(null);
  }

  function actualizarFila(id, cambios) {
    setFilasTransito((prev) => prev.map((f) => (f.id === id ? { ...f, ...cambios } : f)));
  }

  function rechazarFila(id) {
    setFilasTransito((prev) => prev.filter((f) => f.id !== id));
  }

  async function aceptarFila(fila, { refrescar = true } = {}) {
    actualizarFila(fila.id, { guardando: true });
    try {
      const { data: movimientoCreado } = await api.post('/movimientos', {
        cuenta_id: cuentaFiltro,
        categoria_id: fila.categoria_id || null,
        tipo: fila.tipo,
        monto: fila.monto,
        descripcion: fila.descripcion,
        fecha: fila.fecha,
        referencia: fila.referencia || undefined,
        // Un movimiento aceptado desde la importación del estado de cuenta
        // todavía no pasó por revisión humana explícita (#81): arranca
        // "pendiente", nunca "confirmado" por default. Pasar a confirmado
        // es una acción aparte desde la grilla de Movimientos; a
        // "conciliado" solo lo setea el módulo de Conciliación (#79).
        estado: 'pendiente'
      });
      if (fila.regla && String(fila.categoria_id) === fila.regla.categoria_id) {
        api.post(`/categorizacion-reglas/${fila.regla.id}/uso`).catch(() => {});
      }

      // Si el usuario aceptó la sugerencia de pago (checkbox "vincular"), el
      // movimiento recién creado se enlaza al pago pendiente — mismo
      // endpoint que ya usa "Registrar pago" en el estado de cuenta, no hay
      // lógica de conversión/anticipo nueva que mantener acá.
      if (fila.vincularPago && fila.sugerencia) {
        const base = fila.sugerencia.factura_tipo === 'venta' ? '/facturas' : '/compras';
        try {
          await api.put(`${base}/pagos/${fila.sugerencia.pago_id}`, {
            movimiento_id: movimientoCreado.id,
            cuenta_id: cuentaFiltro,
            fecha: fila.fecha,
            monto_original: fila.monto,
            referencia: fila.referencia || undefined
          });
        } catch (errVinculo) {
          // El movimiento ya se creó; si el vínculo falla (ej. el pago se
          // sobrepagaría), se avisa pero no se revierte el movimiento — el
          // usuario puede vincularlo a mano después desde el estado de cuenta.
          alert(
            `El movimiento se creó, pero no se pudo vincular al pago de ${fila.sugerencia.nombre}: ` +
            `${errVinculo.response?.data?.error || 'error desconocido'}. Podés vincularlo a mano desde su estado de cuenta.`
          );
        }
      }

      setFilasTransito((prev) => prev.filter((f) => f.id !== fila.id));
      if (refrescar) cargar();
      return true;
    } catch (err) {
      actualizarFila(fila.id, { guardando: false, error: err.response?.data?.error || 'No se pudo guardar' });
      return false;
    }
  }

  const filtroTransitoNormalizado = filtroTransito.trim().toLowerCase();
  const filasTransitoFiltradas = filtroTransitoNormalizado
    ? filasTransito.filter((f) => {
        const nombreCategoria = categorias.find((c) => String(c.id) === String(f.categoria_id))?.nombre || '';
        return [f.descripcion, f.referencia, nombreCategoria, f.monto, f.fecha]
          .join(' ')
          .toLowerCase()
          .includes(filtroTransitoNormalizado);
      })
    : filasTransito;

  const totalPaginasTransito = Math.max(1, Math.ceil(filasTransitoFiltradas.length / TAMANIO_PAGINA_TRANSITO));
  const paginaTransitoActual = Math.min(paginaTransito, totalPaginasTransito - 1);
  const filasPaginaTransito = filasTransitoFiltradas.slice(
    paginaTransitoActual * TAMANIO_PAGINA_TRANSITO,
    paginaTransitoActual * TAMANIO_PAGINA_TRANSITO + TAMANIO_PAGINA_TRANSITO
  );

  function actualizarFiltroTransito(valor) {
    setFiltroTransito(valor);
    setPaginaTransito(0);
  }

  function rechazarTodasPaginaTransito() {
    const ids = new Set(filasPaginaTransito.map((f) => f.id));
    setFilasTransito((prev) => prev.filter((f) => !ids.has(f.id)));
  }

  async function aceptarTodasPaginaTransito() {
    setProcesandoTodas(true);
    for (const fila of filasPaginaTransito) {
      // eslint-disable-next-line no-await-in-loop
      await aceptarFila(fila, { refrescar: false });
    }
    cargar();
    setProcesandoTodas(false);
  }

  function agruparCategoriasPorTipo(cats) {
    return ['ingreso', 'egreso'].flatMap((tipo) =>
      agruparCategorias(cats, tipo).map((grupo) => ({ ...grupo, tipo }))
    );
  }

  function aplicarCategoriaMasivaTransito() {
    if (!categoriaMasivaTransito) return;
    const cat = categorias.find((c) => String(c.id) === String(categoriaMasivaTransito));
    if (!cat) return;
    const ids = new Set(filasTransitoFiltradas.map((f) => f.id));
    setFilasTransito((prev) =>
      prev.map((f) => (ids.has(f.id) ? { ...f, categoria_id: cat.id, tipo: cat.tipo } : f))
    );
    setCategoriaMasivaTransito('');
  }

  function rechazarTodosTransito() {
    const ids = new Set(filasTransitoFiltradas.map((f) => f.id));
    setFilasTransito((prev) => prev.filter((f) => !ids.has(f.id)));
  }

  async function aceptarTodosTransito() {
    if (
      !window.confirm(
        `¿Aceptar los ${filasTransitoFiltradas.length} movimientos${filtroTransitoNormalizado ? ' que coinciden con el filtro' : ''}? Se van a crear todos de una vez.`
      )
    ) {
      return;
    }
    setProcesandoTodas(true);
    for (const fila of filasTransitoFiltradas) {
      // eslint-disable-next-line no-await-in-loop
      await aceptarFila(fila, { refrescar: false });
    }
    cargar();
    setProcesandoTodas(false);
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este movimiento? Esta acción quedará registrada en la bitácora.')) return;
    try {
      await api.delete(`/movimientos/${id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el movimiento. Revisa que tengas el rol necesario.');
    }
  }

  function ordenarPor(campo) {
    setOrden((o) => (o.campo === campo ? { campo, dir: o.dir === 'asc' ? 'desc' : 'asc' } : { campo, dir: 'asc' }));
  }

  function toggleColumnaVisible(id) {
    setColumnasVisibles((prev) => {
      const siguiente = { ...prev, [id]: !prev[id] };
      try {
        window.localStorage.setItem(claveColumnasVisibles(usuario?.id), JSON.stringify(siguiente));
      } catch (err) {
        // localStorage no disponible (modo privado, etc.) — la preferencia solo dura la sesión
      }
      return siguiente;
    });
  }

  async function cambiarEstadoMovimiento(m) {
    if (m.estado === 'conciliado') return;
    const nuevo = m.estado === 'pendiente' ? 'confirmado' : 'pendiente';
    try {
      await api.patch(`/movimientos/${m.id}/estado`, { estado: nuevo });
      setMovimientos((prev) => prev.map((x) => (x.id === m.id ? { ...x, estado: nuevo } : x)));
      cargarResumenes();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cambiar el estado del movimiento');
    }
  }

  // Un movimiento conciliado o parte de una transferencia no admite acciones
  // en lote (el estado conciliado no se toca a mano, y una transferencia son
  // dos movimientos enlazados que se editan juntos desde su propio botón).
  function puedeSeleccionarLote(m) {
    return m.estado !== 'conciliado' && !m.transferencia_id;
  }

  function toggleSeleccion(id) {
    setSeleccionados((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });
  }

  function toggleSeleccionTodos() {
    const seleccionables = movimientos.filter(puedeSeleccionarLote).map((m) => m.id);
    const todosSeleccionados = seleccionables.length > 0 && seleccionables.every((id) => seleccionados.has(id));
    setSeleccionados(todosSeleccionados ? new Set() : new Set(seleccionables));
  }

  async function aplicarCategoriaLote() {
    if (!categoriaLote || seleccionados.size === 0) return;
    setAplicandoLote(true);
    try {
      await api.patch('/movimientos/lote/categoria', { ids: Array.from(seleccionados), categoria_id: categoriaLote });
      setCategoriaLote('');
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo asignar la categoría a los movimientos seleccionados');
    } finally {
      setAplicandoLote(false);
    }
  }

  async function marcarEstadoLote(estado) {
    if (seleccionados.size === 0) return;
    setAplicandoLote(true);
    try {
      await api.patch('/movimientos/lote/estado', { ids: Array.from(seleccionados), estado });
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cambiar el estado de los movimientos seleccionados');
    } finally {
      setAplicandoLote(false);
    }
  }

  function imprimir() {
    window.print();
  }

  useEffect(() => {
    if (!reciboId) return;
    window.onafterprint = () => setReciboId(null);
    window.print();
    return () => {
      window.onafterprint = null;
    };
  }, [reciboId]);

  async function imprimirRecibo(m) {
    try {
      // El nombre de quien recibe se fija una sola vez por recibo: si ya
      // está guardado, no se vuelve a preguntar ni se puede cambiar.
      let nombreReceptor = m.nombre_receptor;
      if (!nombreReceptor) {
        const nombre = window.prompt(
          'Nombre de la persona que recibe el pago:',
          m.transferencia_id ? m.registrado_por || '' : ''
        );
        if (nombre === null) return;
        if (!nombre.trim()) {
          alert('Debes indicar el nombre de quien recibe el pago.');
          return;
        }
        nombreReceptor = nombre.trim();
      }

      if (!m.numero_recibo || !m.nombre_receptor || (m.transferencia_id && !m.transferencia)) {
        const { data } = await api.post(`/movimientos/${m.id}/recibo`, { nombre_receptor: nombreReceptor });
        setMovimientos((prev) =>
          prev.map((x) =>
            x.id === m.id
              ? { ...x, numero_recibo: data.numero_recibo, nombre_receptor: data.nombre_receptor, transferencia: data.transferencia }
              : x
          )
        );
      }
      setReciboId(m.id);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo generar el recibo');
    }
  }

  function thOrdenable(campo, label, claseExtra = '') {
    const activo = orden.campo === campo;
    return (
      <th
        {...filaClickeable(() => ordenarPor(campo))}
        className={`p-3 print:hidden cursor-pointer select-none hover:text-slate-700 ${FOCO_FILA} ${claseExtra}`}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {activo && (orden.dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
        </span>
      </th>
    );
  }

  function chipEstado(m, { clickeable = true } = {}) {
    const info = ESTADO_MOVIMIENTO_INFO[m.estado] || ESTADO_MOVIMIENTO_INFO.confirmado;
    const puedeClick = clickeable && m.estado !== 'conciliado';
    return (
      <button
        type="button"
        onClick={puedeClick ? () => cambiarEstadoMovimiento(m) : undefined}
        disabled={!puedeClick}
        title={
          m.estado === 'conciliado'
            ? 'Conciliado — para cambiarlo, revertí el vínculo desde Conciliación'
            : `Marcar como ${m.estado === 'pendiente' ? 'confirmado' : 'pendiente'}`
        }
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full whitespace-nowrap transition-colors ${info.clases} ${
          puedeClick ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <info.Icono size={12} />
        {info.label}
      </button>
    );
  }

  async function exportarExcel() {
    setExportando(true);
    try {
      const { data } = await api.get('/reportes/movimientos/excel', {
        params: { q: busqueda || undefined, cuenta_id: cuentaFiltro || undefined },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `movimientos${empresaNombre ? `-${empresaNombre}` : ''}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('No se pudo exportar los movimientos a Excel');
    } finally {
      setExportando(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center print:hidden">
        <h1 className="text-2xl font-bold text-slate-800 shrink-0">
          Movimientos
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>

        {cuentaFiltro && cuentaFiltroSel && (
          <div className="flex items-stretch justify-end gap-4 flex-wrap lg:flex-1">
            <div className="bg-white rounded-lg shadow-sm p-4 min-w-[160px] flex flex-col justify-center">
              <p className="text-xs text-slate-500">Saldo actual</p>
              <p className="text-lg font-bold text-slate-800 whitespace-nowrap">
                {cuentaFiltroSel.moneda} {cuentaFiltroSel.moneda !== 'BS' ? '$' : ''}{formatMonto(cuentaFiltroSel.saldo_actual)}
              </p>
            </div>

            <TarjetaIngresoEgreso titulo="Últimos 30 días" ingresos={resumen30.ingresos} egresos={resumen30.egresos} simbolo={simboloMonedaFiltro} compacto ancho />

            <div className="bg-white rounded-lg shadow-sm p-4 min-w-[160px] flex flex-col justify-center">
              <p className="text-xs text-slate-500">Conciliación</p>
              <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                <p className="text-lg font-bold text-slate-800">{resumenConciliacion.porcentaje}%</p>
                <p className="text-xs text-slate-400">
                  · {resumenConciliacion.por_conciliar} / {resumenConciliacion.total}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-center gap-2">
              {cuentaFiltroInactiva ? (
                <span
                  className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600 whitespace-nowrap"
                  title="Reactívala en Bancos → Cuentas para registrarle movimientos"
                >
                  Inactiva: sin movimientos nuevos
                </span>
              ) : (
                <>
                  <button
                    onClick={abrirFormMovimiento}
                    title="Nuevo movimiento"
                    className="p-2 rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                  <button
                    onClick={abrirTransferencia}
                    title="Nueva transferencia"
                    className="p-2 rounded-md text-slate-900 bg-amber-500 hover:bg-amber-600 transition-colors"
                  >
                    <Repeat size={18} />
                  </button>
                  <button
                    onClick={abrirModalEdoCuenta}
                    title="Cargar estado de cuenta"
                    className="p-2 rounded-md text-white bg-teal-600 hover:bg-teal-700 transition-colors"
                  >
                    <Upload size={18} />
                  </button>
                </>
              )}
              <button
                onClick={imprimir}
                title="Imprimir"
                className="p-2 rounded-md text-white bg-slate-600 hover:bg-slate-700 transition-colors"
              >
                <Printer size={18} />
              </button>
              <button
                onClick={exportarExcel}
                disabled={exportando}
                title={exportando ? 'Exportando...' : 'Exportar a Excel'}
                className="p-2 rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <FileSpreadsheet size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {!reciboId && (
        <div className="hidden print:block">
          <h1 className="text-lg font-bold text-black">
            Movimientos{empresaNombre ? ` · ${empresaNombre}` : ''}{cuentaImpresionUnica ? ` · ${cuentaImpresionUnica}` : ''}
          </h1>
          <p className="text-xs text-black">
            Impreso el {new Date().toLocaleDateString('es')} · Página {pagina} de {totalPaginas} ({total} movimientos en total
            {totalPaginas > 1 ? '; usa "Exportar a Excel" para obtener el listado completo' : ''})
          </p>
        </div>
      )}

      {recibo && (
        <div className="recibo-pago hidden print:block">
          <div className="encabezado">
            <div className="marca">
              <p className="empresa">{empresaNombre}</p>
              <p className="titulo">Recibo de pago</p>
            </div>
            <div className="folio-no">No. {recibo.numero_recibo}</div>
            <div className="fecha">{formatFecha(recibo.fecha)}</div>
          </div>

          <div className="cuerpo">
            <div className="campo">
              <span className="etiqueta">Cuenta</span>
              <span className="valor">{recibo.cuenta_nombre}</span>
            </div>
            {recibo.transferencia ? (
              <div className="campo">
                <span className="etiqueta">Transferencia</span>
                <span className="valor">
                  {recibo.transferencia.empresa_origen_nombre} → {recibo.transferencia.empresa_destino_nombre}
                </span>
              </div>
            ) : (
              <div className="campo">
                <span className="etiqueta">Categoría</span>
                <span className="valor">{recibo.categoria_nombre || '-'}</span>
              </div>
            )}
            <div className="campo full">
              <span className="etiqueta">Concepto</span>
              <span className="valor">{recibo.descripcion || '-'}</span>
            </div>
            <div className="campo monto">
              <span className="etiqueta">Monto</span>
              <span className="valor">{recibo.cuenta_moneda === 'BS' ? 'Bs ' : '$'}{formatMonto(recibo.monto)}</span>
            </div>
          </div>

          <div className="pie">
            <p className="entregado">Entregado por: {usuario?.nombre}</p>
            <div className="firma">
              <div className="firma-espacio"></div>
              <div className="firma-linea"></div>
              <p className="firma-nombre">{recibo.nombre_receptor}</p>
              <p className="firma-caption">Recibí conforme</p>
            </div>
          </div>

          <div className="sello" aria-hidden="true">
            <svg viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="56" fill="none" stroke="#a8352b" strokeWidth="2.5" />
              <circle cx="60" cy="60" r="48" fill="none" stroke="#a8352b" strokeWidth="1.4" />
              <path id="selloArcTop" d="M 18 60 A 42 42 0 0 1 102 60" fill="none" />
              <path id="selloArcBot" d="M 102 60 A 42 42 0 0 1 18 60" fill="none" />
              <text fontFamily="ui-sans-serif, Arial, sans-serif" fontSize="10.5" fontWeight="700" letterSpacing="2.5" fill="#a8352b">
                <textPath href="#selloArcTop" startOffset="50%" textAnchor="middle">TESORERÍA G&amp;F</textPath>
              </text>
              <text fontFamily="ui-sans-serif, Arial, sans-serif" fontSize="10.5" fontWeight="700" letterSpacing="2.5" fill="#a8352b">
                <textPath href="#selloArcBot" startOffset="50%" textAnchor="middle">
                  {(empresaNombre || '').toUpperCase()}
                </textPath>
              </text>
              <text x="60" y="66" textAnchor="middle" fontFamily="Georgia, serif" fontSize="20" fontWeight="700" fill="#a8352b">
                PAGADO
              </text>
            </svg>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <div className="relative w-full sm:w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar movimientos"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="border rounded-md pl-9 pr-3 py-2 w-full text-sm"
          />
        </div>
        <select
          value={cuentaFiltro}
          onChange={(e) => setCuentaFiltro(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm w-full sm:w-48"
        >
          {cuentas.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}{Number(c.activo) === 1 ? '' : ' (inactiva)'}</option>
          ))}
        </select>

        <div className="relative">
          <button
            type="button"
            onClick={() => setMostrarFiltroEstado((v) => !v)}
            title="Filtrar por estado"
            className={`p-2 rounded-md border hover:bg-slate-50 ${
              estadoFiltro ? 'border-primary-400 text-primary-600 bg-primary-50' : 'text-slate-500'
            }`}
          >
            <ListFilter size={18} />
          </button>
          {mostrarFiltroEstado && (
            <div className="absolute z-20 top-full left-0 mt-1 w-48 rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg text-sm">
              {ESTADOS_FILTRO.map((op) => (
                <button
                  key={op.value}
                  type="button"
                  onClick={() => {
                    setEstadoFiltro(op.value);
                    setMostrarFiltroEstado(false);
                  }}
                  className={`w-full text-left px-2.5 py-1.5 rounded-md ${
                    estadoFiltro === op.value ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {op.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setMostrarColumnas((v) => !v)}
            title="Elegir columnas"
            className="p-2 rounded-md border text-slate-500 hover:bg-slate-50"
          >
            <Columns3 size={18} />
          </button>
          {mostrarColumnas && (
            <div className="absolute z-20 top-full left-0 mt-1 w-56 rounded-lg border border-slate-200 bg-white p-3 shadow-lg text-sm space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Columnas visibles</p>
              {COLUMNAS_OPCIONALES.map((col) => (
                <label key={col.id} className="flex items-center gap-2 cursor-pointer text-slate-600">
                  <input
                    type="checkbox"
                    checked={!!columnasVisibles[col.id]}
                    onChange={() => toggleColumnaVisible(col.id)}
                    className="rounded"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="hidden sm:block w-px h-6 bg-slate-200 mx-1" aria-hidden="true" />

        <div className="flex items-center gap-2 sm:ml-auto">
          {hayMovimientosBS && (
            <SelectorMoneda vista={vistaMoneda} onChange={setVistaMoneda} />
          )}
        </div>
      </div>

      {mostrarForm && (
        <Modal title={editandoId ? 'Editando movimiento' : 'Nuevo movimiento'} onClose={cancelar} maxWidth="max-w-3xl" className="print:hidden">
        <form onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {movimientoEditando && (
            <div className="sm:col-span-3 flex items-center gap-2 text-sm text-slate-500">
              <span>Estado:</span>
              {chipEstado(movimientoEditando)}
            </div>
          )}
          <input
            required
            type="date"
            value={form.fecha}
            onChange={(e) => {
              setForm({ ...form, fecha: e.target.value });
              setDuplicados(null);
            }}
            className="border rounded-md px-3 py-2"
          />

          <select
            required
            value={form.tipo}
            onChange={(e) => setForm({ ...form, tipo: e.target.value })}
            className={`border-2 rounded-md px-3 py-2 font-medium ${
              form.tipo === 'ingreso'
                ? 'border-green-400 bg-green-50 text-green-700'
                : 'border-red-400 bg-red-50 text-red-700'
            }`}
          >
            <option value="ingreso">▲ Ingreso</option>
            <option value="egreso">▼ Egreso</option>
          </select>

          <select
            required
            disabled={!movimientoEditando || movimientoEditando.estado === 'conciliado'}
            value={form.cuenta_id}
            onChange={(e) => setForm({ ...form, cuenta_id: e.target.value })}
            title={movimientoEditando?.estado === 'conciliado' ? 'Este movimiento está conciliado; no se puede cambiar de cuenta' : undefined}
            className={`border rounded-md px-3 py-2 ${
              !movimientoEditando || movimientoEditando.estado === 'conciliado'
                ? 'bg-slate-50 text-slate-500 cursor-not-allowed'
                : ''
            }`}
          >
            <option value="">Selecciona cuenta</option>
            {cuentas
              .filter((c) => !movimientoEditando || c.moneda === cuentas.find((x) => String(x.id) === String(movimientoEditando.cuenta_id))?.moneda)
              .filter((c) => Number(c.activo) === 1 || String(c.id) === String(movimientoEditando?.cuenta_id))
              .map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}{Number(c.activo) === 1 ? '' : ' (inactiva)'}</option>
              ))}
          </select>
          {movimientoEditando && movimientoEditando.estado !== 'conciliado' && String(form.cuenta_id) !== String(movimientoEditando.cuenta_id) && (
            <p className="sm:col-span-3 -mt-2 text-sm text-amber-600">
              Este movimiento se moverá de cuenta; el saldo se revierte en la original y se aplica en la nueva.
            </p>
          )}

          <input
            required
            type="number"
            step="0.01"
            placeholder="Monto"
            value={form.monto}
            onChange={(e) => {
              setForm({ ...form, monto: e.target.value });
              setDuplicados(null);
            }}
            className="border rounded-md px-3 py-2"
          />

          {cuentaFormEsBS && (
            <input
              type="number"
              step="0.0001"
              placeholder="Tasa USD opcional"
              value={form.tasa_override}
              onChange={(e) => setForm({ ...form, tasa_override: e.target.value })}
              className="border rounded-md px-3 py-2"
            />
          )}

          <div className="flex flex-col gap-1 min-w-0">
          <select
            value={form.categoria_id}
            onChange={(e) => { setForm({ ...form, categoria_id: e.target.value }); setReglaForm(null); setIaForm(null); }}
            className="border rounded-md px-3 py-2"
          >
            <option value="">Sin categoría</option>
            {agruparCategoriasPorTipo(categorias).map((grupo) => (
              <optgroup
                key={`${grupo.tipo}-${grupo.nombre}`}
                label={`${grupo.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} · ${grupo.nombre}`}
              >
                {grupo.items.map((c) => (
                  <option key={c.id} value={c.id}>
                    {!c.padre_id && grupo.items.length > 1 ? `${etiquetaCategoria(c)} (general)` : etiquetaCategoria(c)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {!editandoId && reglaForm && String(form.categoria_id) === reglaForm.categoria_id && (
            <span className="text-xs text-green-700">Por regla «{reglaForm.texto}»</span>
          )}
          {!editandoId && iaForm && (
            <span className="text-xs text-violet-700" title={iaForm.motivo || ''}>
              {iaForm.categoria_id ? 'Sugerida por IA · confirma que esté bien' : iaForm.motivo}
              {iaForm.categoria_id && puedeEditarMov && (
                <> · <a className="underline" target="_blank" rel="noreferrer"
                  href={`/categorias?reglas=nueva&texto=${encodeURIComponent((clienteFormNombre || form.descripcion || '').slice(0, 100))}&categoria_id=${form.categoria_id}`}>
                  Crear regla con esto
                </a></>
              )}
            </span>
          )}
          {!editandoId && !form.categoria_id && !reglaForm && (form.descripcion || clienteFormNombre).trim().length >= 2 && (
            <button type="button" onClick={sugerirCategoriaIA} disabled={pidiendoIAForm}
              className="self-start inline-flex items-center gap-1 text-xs text-violet-700 hover:underline disabled:opacity-50">
              {pidiendoIAForm ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {pidiendoIAForm ? 'Pidiendo a la IA…' : 'Sugerir con IA'}
            </button>
          )}
          </div>

          {form.tipo === 'ingreso' && (
            <select
              value={form.cliente_id}
              onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
              className="border rounded-md px-3 py-2"
            >
              <option value="">Sin cliente</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          )}

          <input
            placeholder="Descripción"
            value={form.descripcion}
            onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
            className="border rounded-md px-3 py-2 sm:col-span-3"
          />

          <div className="sm:col-span-3 space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-primary-600">
                <Paperclip size={16} />
                <span>{extraidoIA ? extraidoIA.nombre_archivo : archivoAdjunto ? archivoAdjunto.name : 'Adjuntar comprobante (opcional)'}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                  onChange={(e) => {
                    setArchivoAdjunto(e.target.files[0] || null);
                    setExtraidoIA(null);
                    setAvisoIA('');
                    setItemsFactura([]);
                    setProveedorFactura({ modo: 'ninguno', id: '', nombre: '' });
                  }}
                  className="hidden"
                />
              </label>
              {archivoAdjunto && !extraidoIA && (
                <button
                  type="button"
                  onClick={analizarConIA}
                  disabled={analizando}
                  className="text-xs px-2 py-1 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {analizando ? (
                    <>
                      <Loader2 size={12} className="animate-spin" /> Analizando con IA...
                    </>
                  ) : (
                    'Analizar con IA'
                  )}
                </button>
              )}
            </div>
            {avisoIA && <p className="text-xs text-slate-500">{avisoIA}</p>}
            {adjuntos.length > 0 && (
              <ul className="text-sm space-y-1">
                {adjuntos.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => descargarAdjunto(a)}
                      className="text-primary-600 hover:underline inline-flex items-center gap-1"
                    >
                      <Download size={14} /> {a.nombre_archivo}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {itemsFactura.length > 0 && (
            <div className="sm:col-span-3 bg-slate-50 border border-slate-200 rounded-md p-4 space-y-3">
              <p className="text-sm font-medium text-slate-600">
                Líneas de la factura — enlazá cada una a un producto del inventario para registrar su costo (tarea #30.3).
              </p>

              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-500">Proveedor:</span>
                <select
                  value={proveedorFactura.modo === 'existente' ? proveedorFactura.id : proveedorFactura.modo === 'nuevo' ? 'nuevo' : ''}
                  onChange={(e) => {
                    if (e.target.value === 'nuevo') {
                      setProveedorFactura({ modo: 'nuevo', id: '', nombre: proveedorFactura.nombre });
                    } else if (e.target.value === '') {
                      setProveedorFactura({ modo: 'ninguno', id: '', nombre: '' });
                    } else {
                      const p = proveedores.find((x) => String(x.id) === e.target.value);
                      setProveedorFactura({ modo: 'existente', id: p.id, nombre: p.nombre });
                    }
                  }}
                  className="border rounded-md px-2 py-1"
                >
                  <option value="">Sin proveedor</option>
                  {proveedores.map((p) => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                  <option value="nuevo">+ Nuevo proveedor...</option>
                </select>
                {proveedorFactura.modo === 'nuevo' && (
                  <input
                    placeholder="Nombre del proveedor"
                    value={proveedorFactura.nombre}
                    onChange={(e) => setProveedorFactura({ ...proveedorFactura, nombre: e.target.value })}
                    className="border rounded-md px-2 py-1"
                  />
                )}
              </div>

              <div className="space-y-2">
                {itemsFactura.map((item, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-md bg-white p-2">
                    <div className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="text-slate-700">{item.descripcion}</span>
                      <span className="text-slate-400 text-xs whitespace-nowrap">
                        {item.cantidad || '-'} x {item.precio_unitario || '-'}
                      </span>
                    </div>
                    <div className="mt-1.5">
                      <AutocompleteProducto
                        productos={productos}
                        value={item.producto_id}
                        onChange={(id) => {
                          const copia = [...itemsFactura];
                          copia[idx] = { ...copia[idx], producto_id: id };
                          setItemsFactura(copia);
                        }}
                        placeholder="Escribí o seleccioná el producto..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {duplicados && duplicados.length > 0 && (
            <p className="sm:col-span-3 text-amber-700 text-sm bg-amber-50 border border-amber-200 rounded-md p-3">
              Ya existe un movimiento similar el {formatFecha(duplicados[0].fecha)}
              {duplicados[0].descripcion ? ` ("${duplicados[0].descripcion}")` : ''} con la misma cuenta, fecha y monto.
              Si es un comprobante distinto, guardalo de todas formas.
            </p>
          )}

          {error && <p className="text-red-600 text-sm sm:col-span-3">{error}</p>}

          <button type="submit" className="bg-primary-600 text-white rounded-md py-2 sm:col-span-3 hover:bg-primary-700">
            {duplicados && duplicados.length > 0 ? 'Guardar de todas formas' : editandoId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </form>
        </Modal>
      )}

      {mostrarTransferencia && (
        <Modal
          title={editandoTransferenciaId ? 'Editando transferencia' : 'Nueva transferencia entre cuentas'}
          onClose={cancelarTransferencia}
          className="print:hidden"
        >
        <form onSubmit={guardarTransferencia} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <select
            required
            disabled={!editandoTransferenciaId}
            value={formT.cuenta_origen_id}
            onChange={(e) => setFormT({ ...formT, cuenta_origen_id: e.target.value })}
            className={`border rounded-md px-3 py-2 ${!editandoTransferenciaId ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
          >
            <option value="" disabled hidden>
              {editandoTransferenciaId ? 'Cuenta origen (todas las compañías)' : `Cuenta origen${empresaNombre ? ` (${empresaNombre})` : ''}`}
            </option>
            {opcionesCuentaOrigen.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}{c.empresa_nombre ? ` · ${c.empresa_nombre}` : ''}</option>
            ))}
          </select>

          <select
            required
            value={formT.cuenta_destino_id}
            onChange={(e) => setFormT({ ...formT, cuenta_destino_id: e.target.value })}
            className="border rounded-md px-3 py-2"
          >
            <option value="" disabled hidden>Cuenta destino (todas las compañías)</option>
            {cuentasTodas
              .filter((c) => String(c.id) !== String(formT.cuenta_origen_id))
              .map((c) => (
                <option key={c.id} value={c.id}>{c.nombre} · {c.empresa_nombre}</option>
              ))}
          </select>

          <input
            required
            type="number"
            step="0.01"
            placeholder={monedaDistinta ? `Monto que sale (${cuentaOrigenSel?.moneda})` : 'Monto'}
            value={formT.monto_origen}
            onChange={(e) => setFormT({ ...formT, monto_origen: e.target.value })}
            className="border rounded-md px-3 py-2"
          />

          {monedaDistinta && (
            <input
              required
              type="number"
              step="0.01"
              placeholder={`Monto que entra (${cuentaDestinoSel?.moneda})`}
              value={formT.monto_destino}
              onChange={(e) => setFormT({ ...formT, monto_destino: e.target.value })}
              className="border rounded-md px-3 py-2"
            />
          )}

          <input
            required
            type="date"
            value={formT.fecha}
            onChange={(e) => setFormT({ ...formT, fecha: e.target.value })}
            className="border rounded-md px-3 py-2"
          />

          <input
            placeholder="Descripción"
            value={formT.descripcion}
            onChange={(e) => setFormT({ ...formT, descripcion: e.target.value })}
            className="border rounded-md px-3 py-2 sm:col-span-2"
          />

          {monedaDistinta && (
            <p className="sm:col-span-2 text-xs text-amber-600">
              Las cuentas tienen monedas distintas ({cuentaOrigenSel?.moneda} → {cuentaDestinoSel?.moneda}); indica cuánto sale de la cuenta origen y cuánto entra a la cuenta destino según el tipo de cambio usado.
            </p>
          )}

          {errorT && <p className="text-red-600 text-sm sm:col-span-2">{errorT}</p>}

          <button type="submit" className="bg-primary-600 text-white rounded-md py-2 sm:col-span-2 hover:bg-primary-700">
            {editandoTransferenciaId ? 'Guardar cambios' : 'Guardar transferencia'}
          </button>
        </form>
        </Modal>
      )}

      {mostrarModalEdoCuenta && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4 print:hidden">
          <div className={`bg-white rounded-xl shadow-lg w-full max-h-[90vh] overflow-y-auto p-5 ${filasTransito.length > 0 ? 'max-w-6xl' : 'max-w-4xl'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium text-slate-700">
                Cargar estado de cuenta
                {cuentaFiltro && <span className="text-slate-400 font-normal"> · {cuentas.find((c) => String(c.id) === String(cuentaFiltro))?.nombre}</span>}
              </h2>
              <button onClick={cerrarModalEdoCuenta} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            {filasTransito.length === 0 && !hojasExcel ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (analizandoEdoCuenta) return;
                      const archivo = e.dataTransfer.files[0];
                      if (archivo) setArchivoPdfPendiente(archivo);
                    }}
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg py-12 px-3 text-slate-500 cursor-pointer hover:border-primary-400 hover:text-primary-600"
                  >
                    <Upload size={28} />
                    <span className="text-sm text-center">
                      Arrastrá el PDF/foto/Excel del estado de cuenta acá o hacé click (extracción con IA, cualquier formato de banco)
                    </span>
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                      disabled={analizandoEdoCuenta}
                      onChange={(e) => {
                        const archivo = e.target.files[0];
                        if (archivo) setArchivoPdfPendiente(archivo);
                      }}
                      className="hidden"
                    />
                  </label>
                  {archivoPdfPendiente && (
                    <div className="mt-2 flex items-center justify-between gap-2 bg-slate-50 border rounded-md px-3 py-2 text-sm">
                      <span className="truncate text-slate-700" title={archivoPdfPendiente.name}>{archivoPdfPendiente.name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => setArchivoPdfPendiente(null)}
                          disabled={analizandoEdoCuenta}
                          title="Quitar archivo"
                          className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
                        >
                          <X size={16} />
                        </button>
                        <button
                          onClick={() => analizarEstadoCuenta(archivoPdfPendiente)}
                          disabled={analizandoEdoCuenta}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
                        >
                          {analizandoEdoCuenta ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          {analizandoEdoCuenta
                            ? (contadorEdoCuenta > 0 ? `Analizando... ${contadorEdoCuenta} encontrados` : 'Analizando...')
                            : 'Analizar con IA'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (analizandoExcel) return;
                      const archivo = e.dataTransfer.files[0];
                      if (archivo) setArchivoExcelPendiente(archivo);
                    }}
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg py-12 px-3 text-slate-500 cursor-pointer hover:border-teal-400 hover:text-teal-600"
                  >
                    <FileSpreadsheet size={28} />
                    <span className="text-sm text-center">
                      Arrastrá el Excel de plantilla interna acá o hacé click (una pestaña por cuenta, sin IA)
                    </span>
                    <input
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      disabled={analizandoExcel}
                      onChange={(e) => {
                        const archivo = e.target.files[0];
                        if (archivo) setArchivoExcelPendiente(archivo);
                      }}
                      className="hidden"
                    />
                  </label>
                  {archivoExcelPendiente && (
                    <div className="mt-2 flex items-center justify-between gap-2 bg-slate-50 border rounded-md px-3 py-2 text-sm">
                      <span className="truncate text-slate-700" title={archivoExcelPendiente.name}>{archivoExcelPendiente.name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => setArchivoExcelPendiente(null)}
                          disabled={analizandoExcel}
                          title="Quitar archivo"
                          className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
                        >
                          <X size={16} />
                        </button>
                        <button
                          onClick={() => analizarExcelEstadoCuenta(archivoExcelPendiente)}
                          disabled={analizandoExcel}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 whitespace-nowrap"
                        >
                          {analizandoExcel ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          {analizandoExcel ? 'Leyendo...' : 'Leer Excel'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {errorEdoCuenta && <p className="text-red-600 text-sm sm:col-span-2">{errorEdoCuenta}</p>}
                {errorExcel && <p className="text-red-600 text-sm sm:col-span-2">{errorExcel}</p>}
              </div>
            ) : filasTransito.length === 0 && hojasExcel ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-600">
                    Elegí qué pestaña del Excel corresponde a la cuenta{' '}
                    <span className="font-medium text-slate-800">
                      {cuentas.find((c) => String(c.id) === String(cuentaFiltro))?.nombre || 'seleccionada'}
                    </span>
                    . Los movimientos de esa pestaña se van a cargar ahí.
                  </p>
                  <button onClick={() => setHojasExcel(null)} className="text-sm text-primary-600 hover:underline shrink-0 ml-3">
                    Volver
                  </button>
                </div>
                {hojasExcel.length === 0 ? (
                  <p className="text-sm text-slate-500">No se reconoció ninguna pestaña con el formato esperado en este archivo.</p>
                ) : (
                  <div className="divide-y border rounded-lg">
                    {hojasExcel.map((h) => (
                      <button
                        key={h.nombre}
                        onClick={() => elegirHojaExcel(h)}
                        disabled={h.movimientos.length === 0}
                        className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <span className="text-slate-700">{h.nombre}</span>
                        <span className="text-slate-400">{h.movimientos.length} movimiento{h.movimientos.length === 1 ? '' : 's'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <div className="relative max-w-xs flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={filtroTransito}
                      onChange={(e) => actualizarFiltroTransito(e.target.value)}
                      placeholder="Filtrar por descripción, categoría, monto..."
                      className="border rounded-md pl-8 pr-2.5 py-1.5 w-full text-xs"
                    />
                  </div>
                  {filtroTransitoNormalizado && (
                    <>
                      <select
                        value={categoriaMasivaTransito}
                        onChange={(e) => setCategoriaMasivaTransito(e.target.value)}
                        className="border rounded-md px-1.5 py-1.5 text-xs"
                      >
                        <option value="">Cambiar categoría de los filtrados a...</option>
                        {agruparCategoriasPorTipo(categorias).map((grupo) => (
                          <optgroup
                            key={`${grupo.tipo}-${grupo.nombre}`}
                            label={`${grupo.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} · ${grupo.nombre}`}
                          >
                            {grupo.items.map((c) => (
                              <option key={c.id} value={c.id}>
                                {!c.padre_id && grupo.items.length > 1 ? `${etiquetaCategoria(c)} (general)` : etiquetaCategoria(c)}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <button
                        onClick={aplicarCategoriaMasivaTransito}
                        disabled={!categoriaMasivaTransito || filasTransitoFiltradas.length === 0 || procesandoTodas}
                        className="px-3 py-1.5 rounded-md text-white bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-xs font-medium"
                      >
                        Aplicar a los {filasTransitoFiltradas.length} filtrados
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-sm text-slate-500">
                    {filtroTransitoNormalizado
                      ? `${filasTransitoFiltradas.length} de ${filasTransito.length} movimientos coinciden con el filtro`
                      : `${filasTransito.length} movimiento${filasTransito.length === 1 ? '' : 's'} pendiente${filasTransito.length === 1 ? '' : 's'}`}
                    {' · '}página {paginaTransitoActual + 1} de {totalPaginasTransito}
                    {usoIAEdoCuenta && <span> · Análisis con IA{formatCostoIA(usoIAEdoCuenta)}</span>}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setPaginaTransito((p) => Math.max(0, p - 1))}
                      disabled={paginaTransitoActual === 0 || procesandoTodas}
                      className="p-1.5 rounded-md border text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setPaginaTransito((p) => Math.min(totalPaginasTransito - 1, p + 1))}
                      disabled={paginaTransitoActual >= totalPaginasTransito - 1 || procesandoTodas}
                      className="p-1.5 rounded-md border text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Página siguiente"
                    >
                      <ChevronRight size={16} />
                    </button>
                    <button
                      onClick={aceptarTodasPaginaTransito}
                      disabled={procesandoTodas || filasPaginaTransito.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 text-xs font-medium"
                    >
                      {procesandoTodas ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
                      Aceptar los {filasPaginaTransito.length} de esta página
                    </button>
                    <button
                      onClick={rechazarTodasPaginaTransito}
                      disabled={procesandoTodas || filasPaginaTransito.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 text-xs font-medium"
                    >
                      <X size={14} /> Rechazar los {filasPaginaTransito.length} de esta página
                    </button>
                    <span className="w-px h-5 bg-slate-200" />
                    <button
                      onClick={aceptarTodosTransito}
                      disabled={procesandoTodas || filasTransitoFiltradas.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white bg-green-700 hover:bg-green-800 disabled:opacity-50 text-xs font-medium"
                    >
                      {procesandoTodas ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
                      Aceptar todos ({filasTransitoFiltradas.length})
                    </button>
                    <button
                      onClick={rechazarTodosTransito}
                      disabled={procesandoTodas || filasTransitoFiltradas.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white bg-red-700 hover:bg-red-800 disabled:opacity-50 text-xs font-medium"
                    >
                      <X size={14} /> Rechazar todos ({filasTransitoFiltradas.length})
                    </button>
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="p-1.5">Fecha</th>
                      <th className="p-1.5">Referencia</th>
                      <th className="p-1.5">Tipo</th>
                      <th className="p-1.5">Descripción</th>
                      <th className="p-1.5">Categoría</th>
                      <th className="p-1.5 text-right">Monto</th>
                      <th className="p-1.5"></th>
                      <th className="p-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filasPaginaTransito.map((f) => (
                      <tr key={f.id} className="border-t align-top hover:bg-slate-50 transition-colors">
                        <td className="p-1.5">
                          <input
                            type="date"
                            value={f.fecha}
                            onChange={(e) => actualizarFila(f.id, { fecha: e.target.value, duplicado: null })}
                            className="border rounded-md px-1.5 py-1 w-28 text-xs"
                          />
                        </td>
                        <td className="p-1.5 font-mono text-slate-500 whitespace-nowrap">{f.referencia || '—'}</td>
                        <td className="p-1.5">
                          <select
                            value={f.tipo}
                            onChange={(e) => actualizarFila(f.id, { tipo: e.target.value })}
                            className="border rounded-md px-1.5 py-1 text-xs"
                          >
                            <option value="ingreso">Ingreso</option>
                            <option value="egreso">Egreso</option>
                          </select>
                        </td>
                        <td className="p-1.5 min-w-[380px]">
                          <input
                            value={f.descripcion}
                            onChange={(e) => actualizarFila(f.id, { descripcion: e.target.value })}
                            className="border rounded-md px-1.5 py-1 w-full text-xs"
                          />
                          {f.sugerencia && (
                            <label className="mt-1 flex items-center gap-1.5 text-green-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={f.vincularPago}
                                onChange={(e) => actualizarFila(f.id, { vincularPago: e.target.checked })}
                                className="rounded"
                              />
                              <Link2 size={12} className="shrink-0" />
                              <span className="truncate">Vincular pago de {f.sugerencia.nombre}</span>
                            </label>
                          )}
                          {f.duplicado && (
                            <p className="text-amber-600 text-xs mt-1 flex items-center gap-1">
                              <AlertTriangle size={12} /> Posible duplicado
                            </p>
                          )}
                          {f.error && <p className="text-red-600 text-xs mt-1">{f.error}</p>}
                        </td>
                        <td className="p-1.5">
                          <select
                            value={f.categoria_id}
                            onChange={(e) => actualizarFila(f.id, { categoria_id: e.target.value })}
                            className="border rounded-md px-1.5 py-1 text-xs"
                          >
                            <option value="">Sin categoría</option>
                            {agruparCategoriasPorTipo(categorias).map((grupo) => (
                              <optgroup
                                key={`${grupo.tipo}-${grupo.nombre}`}
                                label={`${grupo.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} · ${grupo.nombre}`}
                              >
                                {grupo.items.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {!c.padre_id && grupo.items.length > 1 ? `${etiquetaCategoria(c)} (general)` : etiquetaCategoria(c)}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          {f.regla && String(f.categoria_id) === f.regla.categoria_id && (
                            <span title={`Puesta por la regla «${f.regla.texto}»`}
                              className="ml-1 text-[10px] font-semibold uppercase tracking-wide rounded px-1 py-0.5 bg-green-50 text-green-700">
                              Regla
                            </span>
                          )}
                        </td>
                        <td className="p-1.5">
                          <input
                            type="number"
                            step="0.01"
                            value={f.monto}
                            onChange={(e) => actualizarFila(f.id, { monto: e.target.value, duplicado: null })}
                            className="border rounded-md px-1.5 py-1 w-24 text-right text-xs"
                          />
                        </td>
                        <td className="p-1.5">
                          <button
                            onClick={() => aceptarFila(f)}
                            disabled={f.guardando || procesandoTodas}
                            title="Aceptar"
                            className="p-1.5 rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                          >
                            <Check size={16} />
                          </button>
                        </td>
                        <td className="p-1.5">
                          <button
                            onClick={() => rechazarFila(f.id)}
                            disabled={f.guardando || procesandoTodas}
                            title="Rechazar"
                            className="p-1.5 rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                          >
                            <X size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {seleccionados.size > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-3 flex flex-wrap items-center gap-2 print:hidden">
          <span className="text-sm font-medium text-slate-600 mr-1">
            {seleccionados.size} seleccionado{seleccionados.size === 1 ? '' : 's'}
          </span>
          {puedeEditarMov && (
            <>
              <select
                value={categoriaLote}
                onChange={(e) => setCategoriaLote(e.target.value)}
                disabled={aplicandoLote}
                className="border rounded-md px-1.5 py-1.5 text-xs"
              >
                <option value="">Cambiar categoría a...</option>
                {agruparCategoriasPorTipo(categorias).map((grupo) => (
                  <optgroup
                    key={`${grupo.tipo}-${grupo.nombre}`}
                    label={`${grupo.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} · ${grupo.nombre}`}
                  >
                    {grupo.items.map((c) => (
                      <option key={c.id} value={c.id}>
                        {!c.padre_id && grupo.items.length > 1 ? `${etiquetaCategoria(c)} (general)` : etiquetaCategoria(c)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <button
                onClick={aplicarCategoriaLote}
                disabled={!categoriaLote || aplicandoLote}
                className="px-3 py-1.5 rounded-md text-white bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-xs font-medium"
              >
                Aplicar categoría
              </button>
            </>
          )}
          <button
            onClick={() => marcarEstadoLote('pendiente')}
            disabled={aplicandoLote}
            className="px-3 py-1.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
          >
            Marcar pendiente
          </button>
          <button
            onClick={() => marcarEstadoLote('confirmado')}
            disabled={aplicandoLote}
            className="px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50"
          >
            Marcar confirmado
          </button>
          <button
            type="button"
            onClick={() => setSeleccionados(new Set())}
            title="Cancelar selección"
            className="ml-auto text-slate-400 hover:text-red-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div className={`bg-white rounded-xl shadow-sm overflow-hidden print:shadow-none print:rounded-none ${reciboId ? 'print:hidden' : ''}`}>
        <div className="overflow-x-auto print:overflow-visible">
          <table className="w-full text-sm print:text-[10px] print:table-fixed">
            <thead className="bg-slate-50 text-slate-500 text-left print:bg-transparent">
              <tr>
                <th className="p-3 print:hidden w-8">
                  <input
                    type="checkbox"
                    checked={
                      movimientos.filter(puedeSeleccionarLote).length > 0 &&
                      movimientos.filter(puedeSeleccionarLote).every((m) => seleccionados.has(m.id))
                    }
                    onChange={toggleSeleccionTodos}
                    className="rounded"
                    title="Seleccionar todos los de esta página"
                  />
                </th>
                {thOrdenable('fecha', 'Fecha', 'print:p-1 print:w-[12%]')}
                {columnasVisibles.cuenta && thOrdenable('cuenta', 'Cuenta')}
                {columnasVisibles.categoria && thOrdenable('categoria', 'Categoría')}
                {columnasVisibles.tipo && thOrdenable('tipo', 'Tipo')}
                {columnasVisibles.estado && thOrdenable('estado', 'Estado')}
                {columnasVisibles.cliente && thOrdenable('cliente', 'Cliente')}
                {columnasVisibles.referencia && thOrdenable('referencia', 'Referencia')}
                {columnasVisibles.registrado_por && thOrdenable('registrado_por', 'Registrado por')}
                <th className="p-3 print:p-1 print:w-[70%]">Descripción</th>
                {thOrdenable('monto', 'Monto', 'text-right print:p-1 print:w-[18%]')}
                {hayMovimientosBS && (
                  <th className="p-3 text-right print:hidden">{vistaTasa === 'bcv' ? 'BCV' : 'USD'}</th>
                )}
                <th className="p-3 print:hidden"></th>
              </tr>
            </thead>
            <tbody>
              {movimientos.map((m) => (
                <tr key={m.id} className="border-t hover:bg-slate-50 transition-colors">
                  <td className="p-3 print:hidden">
                    <input
                      type="checkbox"
                      checked={seleccionados.has(m.id)}
                      disabled={!puedeSeleccionarLote(m)}
                      onChange={() => toggleSeleccion(m.id)}
                      title={
                        m.transferencia_id
                          ? 'Las transferencias no admiten acciones en lote'
                          : m.estado === 'conciliado'
                          ? 'Conciliado — no se puede seleccionar para acciones en lote'
                          : undefined
                      }
                      className="rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="p-3 print:p-1 whitespace-nowrap">{formatFecha(m.fecha)}</td>
                  {columnasVisibles.cuenta && (
                    <td className="p-3 print:hidden whitespace-nowrap">{m.cuenta_nombre}</td>
                  )}
                  {columnasVisibles.categoria && (
                    <td className="p-3 print:hidden whitespace-nowrap">
                      {m.transferencia_id ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                          <Repeat size={12} /> Transferencia
                        </span>
                      ) : (
                        m.categoria_nombre || '-'
                      )}
                    </td>
                  )}
                  {columnasVisibles.tipo && <td className="p-3 print:hidden capitalize">{m.tipo}</td>}
                  {columnasVisibles.estado && <td className="p-3 print:hidden">{chipEstado(m)}</td>}
                  {columnasVisibles.cliente && (
                    <td className="p-3 print:hidden whitespace-nowrap">{m.cliente_nombre || '-'}</td>
                  )}
                  {columnasVisibles.referencia && (
                    <td className="p-3 print:hidden font-mono text-xs whitespace-nowrap">{m.referencia || '-'}</td>
                  )}
                  {columnasVisibles.registrado_por && (
                    <td className="p-3 print:hidden whitespace-nowrap">{m.registrado_por || '-'}</td>
                  )}
                  <td className="p-3 print:p-1 max-w-[220px] truncate print:whitespace-normal print:max-w-none print:line-clamp-2">{m.descripcion}</td>
                  <td className={`p-3 print:p-1 text-right font-medium whitespace-nowrap ${m.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="hidden print:inline">{m.tipo === 'ingreso' ? '+' : '-'}</span>
                    {m.cuenta_moneda === 'BS' ? 'Bs ' : '$'}{formatMonto(m.monto)}
                  </td>
                  {hayMovimientosBS && (
                    <td className="p-3 print:hidden text-right whitespace-nowrap text-slate-600">
                      {m.cuenta_moneda === 'BS' ? (valorConvertido(m) !== null ? `$${formatMonto(valorConvertido(m))}` : '—') : ''}
                    </td>
                  )}
                  <td className="p-3 print:hidden">
                    <div className="flex items-center gap-3">
                      {m.transferencia_id || m.tipo === 'egreso' ? (
                        <button
                          onClick={() => imprimirRecibo(m)}
                          title={m.numero_recibo ? `Imprimir recibo ${m.numero_recibo}` : 'Generar e imprimir recibo'}
                          className="text-slate-400 hover:text-primary-600 transition-colors"
                        >
                          <Receipt size={16} />
                        </button>
                      ) : (
                        <span className="inline-block w-4" aria-hidden="true" />
                      )}
                      {m.transferencia_id || puedeEditarMov ? (
                        <button
                          onClick={() => (m.transferencia_id ? empezarEdicionTransferencia(m) : empezarEdicion(m))}
                          title={m.transferencia_id ? 'Editar transferencia' : 'Editar'}
                          className="text-slate-400 hover:text-primary-600 transition-colors"
                        >
                          <Pencil size={16} />
                        </button>
                      ) : (
                        <span className="inline-block w-4" aria-hidden="true" />
                      )}
                      {!m.transferencia_id && Number(cuentas.find((c) => String(c.id) === String(m.cuenta_id))?.activo ?? 1) === 1 ? (
                        <button
                          onClick={() => duplicarMovimiento(m)}
                          title="Duplicar"
                          className="text-slate-400 hover:text-primary-600 transition-colors"
                        >
                          <Copy size={16} />
                        </button>
                      ) : (
                        <span className="inline-block w-4" aria-hidden="true" />
                      )}
                      {(m.transferencia_id || puedeEditarMov) && (
                        <button
                          onClick={() => (m.transferencia_id ? eliminarTransferencia(m.transferencia_id) : eliminar(m.id))}
                          title={m.transferencia_id ? 'Eliminar transferencia' : 'Eliminar'}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="print:hidden">
          <Paginacion pagina={pagina} totalPaginas={totalPaginas} total={total} onCambiarPagina={setPagina} />
        </div>
      </div>
    </div>
  );
}
